import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

const inputCls = "w-full bg-cream border border-forest/20 px-4 py-3.5 text-sm text-charcoal focus:border-forest outline-none transition-colors";

function formatDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Sign-in failed. Please try again.";
}

export default function AdminLogin() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user?.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/auth/login", { email: email.trim(), password });
      setUser(data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-forest text-ivory flex items-center justify-center px-5 py-16 relative overflow-hidden grain" data-testid="admin-login-page">
      <BrandLogo variant="mark" tone="light" className="pointer-events-none select-none absolute -left-32 -bottom-40 w-[620px] opacity-[0.06]" />
      <div className="relative w-full max-w-md">
        <div className="text-center">
          <BrandLogo tone="light" className="h-24 w-auto mx-auto" />
          <p className="mt-8 eyebrow eyebrow-plain justify-center text-champagne">Admin workspace</p>
          <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl leading-tight">Sign in to ELA Rituals</h1>
          <p className="mt-3 text-sm text-ivory/60 font-light">For ELA team members only. Members of the community sign in with Google on the main site.</p>
        </div>
        <form onSubmit={submit} className="mt-10 border border-ivory/15 p-1" data-testid="admin-login-form">
          <div className="bg-ivory text-charcoal p-7 space-y-5">
            <label className="block"><span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Email</span>
              <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${inputCls}`} data-testid="admin-email" /></label>
            <label className="block"><span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Password</span>
              <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={`mt-2 ${inputCls}`} data-testid="admin-password" /></label>
            {error && <p role="alert" className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={busy} className="btn btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit"><Lock size={14} /> {busy ? "Signing in…" : "Sign in"}</button>
          </div>
        </form>
        <p className="mt-8 text-center"><Link to="/" className="text-[11px] uppercase tracking-[0.22em] text-ivory/60 hover:text-ivory link-underline">Back to site</Link></p>
      </div>
    </div>
  );
}
